import { uniq } from 'lodash';
import { useAccessorialRequests } from 'utils/AccessorialRequestWebAPIUtils';
import {
  getAccessorialRequestRule,
  getCarrierAccessorialRequestRule,
} from 'utils/UserServiceAPIUtils';
import { isStopCancelled } from 'helpers/stops';
import { rule } from 'postcss';

export async function getShipperAccessorialDocumentRequirements(shipment) {
  const query = {
    shipment: shipment._id || shipment,
    shipper_status: { $nin: ['canceled', 'declined'] },
  };
  const data = await useAccessorialRequests(query);
  if (!data?.success) {
    throw new Error('ERROR_RETRIEVING_CURRENT_ACCESSORIAL_REQUESTS');
  }
  const { request } = data;
  const accessorialDocumentRulesRequired = uniq(
    request.map((r) => r.accessorial_type),
  );
  const shipperId = shipment.shipper?._id || shipment.shipper;
  const rules = await getAccessorialRequestRule(shipperId, {
    accessorials: accessorialDocumentRulesRequired,
  });
  if (!rules?.data?.getAccessorialRequestRule?.items) {
    throw new Error('ERROR_RETRIEVING_ACCESSORIAL_DOCUMENT_REQUIREMENTS');
  }
  const { items: accessorialRequirements } =
    rules.data.getAccessorialRequestRule;

  const accessorialDocumentRequirements = accessorialRequirements.reduce(
    (allDocuments, accessorial) => {
      return allDocuments.concat(accessorial?.rules.map((rule) => rule.value));
    },
    [],
  );

  return accessorialDocumentRequirements;
}

export async function getCarrierAccessorialDocumentRequirements(shipment) {
  const query = {
    shipment: shipment._id || shipment,
    carrier_status: { $ne: 'declined' },
  };
  const data = await useAccessorialRequests(query);
  if (!data?.success) {
    throw new Error('ERROR_RETRIEVING_CURRENT_ACCESSORIAL_REQUESTS');
  }
  const { request } = data;

  const accessorialDocumentRulesRequired = uniq(
    request.map((r) => r.accessorial_type),
  );

  const shipperId = shipment.shipper?._id || shipment.shipper;
  const rules = await getCarrierAccessorialRequestRule(
    shipperId,
    accessorialDocumentRulesRequired,
  );

  if (!rules?.data?.getCombinedAccessorialRequestRule?.carrierItems) {
    throw new Error(
      'ERROR_RETRIEVING_CARRIER_ACCESSORIAL_DOCUMENT_REQUIREMENTS',
    );
  }
  const { carrierItems: accessorialRequirements } =
    rules.data.getCombinedAccessorialRequestRule;

  const accessorialDocumentRequirements = accessorialRequirements.reduce(
    (allDocuments, accessorial) => {
      return allDocuments.concat(accessorial?.rules.map((rule) => rule.value));
    },
    [],
  );

  return accessorialDocumentRequirements;
}


export async function getAllShipperDocumentRequirements(
  shipment,
  stopDocumentsConfig,
) {
  const accessorialDocumentRequirements =
    await getShipperAccessorialDocumentRequirements(shipment);
  /*
      Get documents required according to our movement type and shipment type and then gather the document requirements per the stops on our shipment
    */
  const { order_type, shipment_type, stops } = shipment;
  const shipmentTypeKey = `${order_type.toUpperCase()}_${shipment_type.toUpperCase()}`;

  const activeStops = stops.filter((stop) => !isStopCancelled(stop));

  const stopKeys = _getStopTypeKeysRequired(activeStops);

  const stopDocumentRequirements = stopKeys.reduce(
    (allStopDocRequirements, stopKey) => {
      const orderType = stopDocumentsConfig[shipmentTypeKey];
      if (!(orderType && orderType[stopKey])) {
        return allStopDocRequirements;
      }
      return allStopDocRequirements.concat(
        stopDocumentsConfig[shipmentTypeKey][stopKey],
      );
    },
    [],
  );

  return uniq(accessorialDocumentRequirements.concat(stopDocumentRequirements));
}

export const getAllShipperAndCarrierStopDocumentRequirements = async (
  shipment,
  stopDocumentsConfig,
) => {
  const shipperAccessorialDocumentRequirements =
    await getShipperAccessorialDocumentRequirements(shipment);

  const carrierAccessorialDocumentRequirements =
    await getCarrierAccessorialDocumentRequirements(shipment);

  const { order_type, order_movement_type, shipment_type, stops } = shipment;

  const activeStops = stops.filter((stop) => !isStopCancelled(stop));

  let shipmentTypeKeys = [];
  if (order_type === 'domestic') {
    shipmentTypeKeys = [
      `${order_type?.toUpperCase()}_${order_movement_type?.toUpperCase()}_${shipment_type?.toUpperCase()}`,
      `${order_type?.toUpperCase()}_${shipment_type?.toUpperCase()}`,
    ];
  } else {
    shipmentTypeKeys = [
      `${order_type?.toUpperCase()}_${shipment_type?.toUpperCase()}`,
    ];
  }

  const stopKeys = _getStopTypeKeysRequired(activeStops);

  const stopDocumentRequirements = stopKeys.reduce(
    (stopDocs, stopKey) => {
      shipmentTypeKeys.forEach((key) => {
        const orderType = stopDocumentsConfig[key];
        if (!(orderType && orderType[stopKey])) {
          return;
        }
        orderType[stopKey]?.forEach(({ document, userType }) => {
          if (userType === 'CARRIER') {
            stopDocs.carrierDocs.push(document);
          } else {
            stopDocs.shipperDocs.push(document);
          }
        });
      });
      return stopDocs;
    },
    { shipperDocs: [], carrierDocs: [] },
  );

  const { shipperDocs, carrierDocs } = stopDocumentRequirements;

  const allShipperDocs = uniq(
    shipperAccessorialDocumentRequirements.concat(shipperDocs),
  );

  const allCarrierDocs = uniq(
    carrierAccessorialDocumentRequirements.concat(carrierDocs),
  );

  const combinedDocs = uniq(allShipperDocs.concat(allCarrierDocs));
  return [combinedDocs, allCarrierDocs, allShipperDocs];
};

function _getStopTypeKeysRequired(stops) {
  return uniq(
    stops.reduce((keys, stop) => {
      const { off_site, action, empty } = stop;

      let key = '';

      if (off_site) {
        key += 'OFFSITE';
      }

      if (empty) {
        key += key === '' ? 'EMPTY' : '_EMPTY';
      } else {
        key += key === '' ? 'LOADED' : '_LOADED';
      }

      const stopAction = action.toUpperCase();

      key += key === '' ? `${stopAction}` : `_${stopAction}`;

      return keys.concat(key);
    }, []),
  );
}
